import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/ui/PageHero';
import Accordion from '../components/ui/Accordion';
import SectionHeader from '../components/ui/SectionHeader';

function Support() {
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = process.env.REACT_APP_API_URL;
      const response = await fetch(`${url}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 4000);
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error(err);
      alert('Something went wrong.');
    }
  };

  const contactMethods = [
    { icon: '💬', title: 'In-App Support', desc: 'Fastest support directly inside Strapin.', detail: 'Settings → Contact Support' },
    { icon: '📧', title: 'Email Support', desc: 'For bugs, feedback, and account help.', detail: 'support@strapinapp.com' },
    { icon: '🛡️', title: 'Safety Reports', desc: 'Report abuse or suspicious activity.', detail: 'Reviewed within 24 hours' },
  ];

  const faqs = [
    { q: 'Is Strapin free to use?', a: 'Yes. Strapin is free to download and use. Premium features may be introduced later.' },
    { q: 'Does Strapin sell my data?', a: 'We do not sell personal information for money. Our ad-supported experience may use advertising partners and device identifiers as described in our Privacy Policy; you can manage tracking choices in your device settings.' },
    {
      q: 'How do I report someone?',
      a: 'From their profile or a 1:1 chat (⋮ menu) → Report User → pick a category → add details if asked → submit. You can block in the same flow. Our team reviews all safety reports.',
    },
    { q: 'How do I delete my account?', a: 'Go to Profile → Settings → Delete Account.' },
    { q: 'How quickly will support reply?', a: 'Usually within 24 hours.' },
  ];

  return (
    <div className="min-h-screen bg-onyx text-mist">
      <Header />

      <PageHero
        tag="Fast Response • Real Support"
        title="Need Help?"
        titleAccent="We're Here For You"
        description="Questions, bugs, feedback, safety concerns or account issues — reach out and our team will help you quickly."
      >
        <a href="#contact" className="btn-primary">Contact Support</a>
        <a href="#faq" className="btn-ghost">View FAQ</a>
      </PageHero>

      <section className="bg-charcoal py-20">
        <div className="site-container">
          <SectionHeader tag="Support Channels" title="Choose Your" titleAccent="Preferred Way" description="Multiple ways to get support depending on your issue." />
          <div className="grid gap-4 md:grid-cols-3">
            {contactMethods.map((item) => (
              <article key={item.title} className="glass-card-hover p-6">
                <div className="mb-3 text-3xl">{item.icon}</div>
                <h3 className="mb-2 font-bold text-white">{item.title}</h3>
                <p className="mb-3 text-sm leading-relaxed text-silver">{item.desc}</p>
                <p className="text-sm font-bold text-primary">{item.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-20">
        <div className="site-container">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <div>
              <SectionHeader
                tag="Contact Us"
                title="Send us a"
                titleAccent="message"
                description="Need help with your account, bugs, privacy, or safety concerns? Fill out the form and we'll get back within 24 hours."
                align="left"
              />
              <div className="space-y-4">
                {[
                  { icon: '⚡', title: 'Fast Response', desc: 'Most queries are answered within 24 hours.' },
                  { icon: '🔒', title: 'Private & Secure', desc: 'Your support requests stay private and confidential.' },
                ].map((item) => (
                  <div key={item.title} className="glass-card flex gap-4 p-5">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/[0.05] text-xl">{item.icon}</span>
                    <div>
                      <h4 className="font-bold text-white">{item.title}</h4>
                      <p className="text-sm text-silver">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card p-6 md:p-8">
              {submitted ? (
                <div className="py-12 text-center">
                  <div className="mb-3 text-4xl">✅</div>
                  <h3 className="mb-2 font-bold text-white">Message Sent</h3>
                  <p className="text-silver">We'll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {[
                    { name: 'name', label: 'Name', type: 'text', placeholder: 'Your name' },
                    { name: 'email', label: 'Email', type: 'email', placeholder: 'Your email' },
                    { name: 'subject', label: 'Subject', type: 'text', placeholder: 'Subject' },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-silver">{field.label}</label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        required
                        className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition focus:border-primary"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-silver">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Write your message..."
                      required
                      rows={5}
                      className="w-full resize-y rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition focus:border-primary"
                    />
                  </div>
                  <button type="submit" className="btn-primary w-full justify-center py-4">Send Message</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="bg-charcoal py-20">
        <div className="site-container max-w-3xl">
          <SectionHeader tag="FAQ" title="Common" titleAccent="Questions" />
          <Accordion
            items={faqs.map((f) => ({ title: f.q, content: <p className="text-silver leading-relaxed">{f.a}</p> }))}
            openIndex={openFaq}
            onToggle={setOpenFaq}
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Support;
